import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HiArrowUpRight, HiArrowLeft, HiArrowRight } from "react-icons/hi2";
import { usePublicBlogPosts } from "../../../../hooks/usePublicBlogPosts";
import { getApiErrorMessage } from "../../../../utils/apiResponse";
import { mapBlogListItem } from "../../../../utils/blog";
import { useLanguage } from "../../../../context/LanguageContext";
import { usePrefetchPublicRoute } from "../../../../hooks/usePrefetchPublicRoute";
import "./Posts.css";

const Posts = ({ activeFilter, onFiltersReady }) => {
  const { language, meta } = useLanguage();
  const prefetchRoute = usePrefetchPublicRoute();
  const postsQuery = usePublicBlogPosts();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 7;

  const posts = useMemo(
    () => (postsQuery.data || []).map((p) => mapBlogListItem(p, language)),
    [postsQuery.data, language]
  );

  const loading = postsQuery.isPending && !postsQuery.data;
  const error = postsQuery.error ? getApiErrorMessage(postsQuery.error) : "";

  useEffect(() => {
    if (postsQuery.data) {
      const categories = [...new Set(posts.map((p) => p.category).filter(Boolean))];
      onFiltersReady?.(categories);
    } else if (postsQuery.error) {
      onFiltersReady?.([]);
    }
  }, [posts, postsQuery.data, postsQuery.error, onFiltersReady]);

  const filteredPosts = useMemo(() => {
    if (!activeFilter || activeFilter === "All") return posts;
    return posts.filter((post) => post.category === activeFilter);
  }, [activeFilter, posts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, language]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / pageSize));
  const pagePosts = filteredPosts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const featuredPost = pagePosts.find((post) => post.featured) || pagePosts[0];
  const gridPosts = pagePosts.filter((post) => post.id !== featuredPost?.id);

  if (loading) {
    return (
      <section className="news-posts" dir={meta.dir}>
        <div className="news-posts__container">
          <p className="news-posts__empty">Loading news…</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="news-posts" dir={meta.dir}>
        <div className="news-posts__container">
          <p className="news-posts__empty" style={{ color: "#b91c1c" }}>
            {error}
          </p>
        </div>
      </section>
    );
  }

  if (!featuredPost) {
    return (
      <section className="news-posts" dir={meta.dir}>
        <div className="news-posts__container">
          <p className="news-posts__empty">No posts found for this filter.</p>
        </div>
      </section>
    );
  }

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <section className="news-posts" dir={meta.dir}>
      <div className="news-posts__container">
        <Link
          to={`/news/${featuredPost.slug}`}
          className="news-posts__featured"
          onMouseEnter={() => prefetchRoute(`/news/${featuredPost.slug}`)}
          onFocus={() => prefetchRoute(`/news/${featuredPost.slug}`)}
        >
          <img
            src={featuredPost.image}
            alt={featuredPost.imageAlt}
            className="news-posts__featured-image"
          />
          <div className="news-posts__meta">
            <span className="news-posts__tag">{featuredPost.category}</span>
            <time className="news-posts__date">{featuredPost.date}</time>
          </div>
          <div className="news-posts__title-row">
            <h2 className="news-posts__featured-title">{featuredPost.title}</h2>
            <HiArrowUpRight className="news-posts__arrow" aria-hidden="true" />
          </div>
          <p className="news-posts__featured-text">{featuredPost.description}</p>
        </Link>

        {gridPosts.length > 0 && (
          <div className="news-posts__grid">
            {gridPosts.map((post) => (
              <Link
                key={post.id}
                to={`/news/${post.slug}`}
                className="news-posts__card"
                onMouseEnter={() => prefetchRoute(`/news/${post.slug}`)}
                onFocus={() => prefetchRoute(`/news/${post.slug}`)}
              >
                <img
                  src={post.image}
                  alt={post.imageAlt}
                  className="news-posts__card-image"
                />
                <div className="news-posts__meta">
                  <span className="news-posts__tag">{post.category}</span>
                  <time className="news-posts__date">{post.date}</time>
                </div>
                <div className="news-posts__title-row">
                  <h3 className="news-posts__card-title">{post.title}</h3>
                  <HiArrowUpRight
                    className="news-posts__arrow news-posts__arrow--sm"
                    aria-hidden="true"
                  />
                </div>
                <p className="news-posts__card-text">{post.description}</p>
              </Link>
            ))}
          </div>
        )}

        {totalPages > 1 ? (
          <div className="news-posts__pagination">
            <button
              type="button"
              className="news-posts__page-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              <HiArrowLeft />
            </button>
            {pageNumbers.map((n) => (
              <button
                key={n}
                type="button"
                className={`news-posts__page-btn ${
                  currentPage === n ? "news-posts__page-btn--active" : ""
                }`}
                onClick={() => setCurrentPage(n)}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              className="news-posts__page-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              <HiArrowRight />
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default Posts;
